import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    paymentId: string;
    onSuccess: () => void;
}

const PaymentReversalDialog: React.FC<Props> = ({ open, onClose, paymentId, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const handleSubmit = async () => {
        if (!reason.trim()) {
            showApiError(new Error('Reason required'), 'Please provide a reason for reversal');
            return;
        }
        setSubmitting(true);
        try {
            await PayrollService.reversePayment(paymentId, { reason: reason.trim() });
            showSuccess('Payment reversed');
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, 'Failed to reverse payment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.revForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Reverse Payment</DialogTitle>
            <DialogContent dividers data-help-section="payroll.revForm.section.details">
                <Box sx={{ pt: 1 }}>
                    <TextField
                        data-help="payroll.revForm.field.reason" label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        autoFocus
                        inputProps={{ maxLength: 500 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.revForm.cancel" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    data-help="payroll.revForm.save"
                    {...helpWhy(submitting ? { kind: 'status', message: 'Saving is in progress.' } : !reason.trim() ? { kind: 'status', message: 'Enter a reason first.' } : null)}
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    disabled={submitting || !reason.trim()}
                >
                    {submitting ? 'Reversing...' : 'Reverse'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentReversalDialog;
